import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Typography, Space, Spin, message, Button, Modal, Form, Input, Select, Tag, Popconfirm, Tooltip } from 'antd';
import {
  MessageOutlined,
  PlusOutlined,
  UserOutlined,
  TagOutlined,
  CheckCircleOutlined,
  SendOutlined,
  EditOutlined,
  DeleteOutlined,
  UndoOutlined,
  UnorderedListOutlined,
  ArrowLeftOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { discussionApi, type Thread, type InboxItem, type Comment } from '@/api/discussionApi';
import { opportunityApi } from '@/api/opportunityApi';
import { userApi } from '@/api/userApi';
import PageHeader from '@/components/ui/PageHeader';
import EmptyState from '@/components/ui/EmptyState';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Text } = Typography;

type SortKey = 'recent' | 'oldest' | 'unresolved';

// Normalized entry used to render the left-hand list across all three tabs.
interface ListEntry {
  id: string;
  subject: string;
  resolved: boolean;
  count: number;
  ts: string;
  metaLeft: string;
  avatarText: string;
  avatarColor: string;
  preview?: string;
  prefix?: string;
}

const labelize = (s?: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '');

const getInitials = (name?: string) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const Avatar = ({ text, size = 32, color = 'var(--color-primary)' }: { text: string; size?: number; color?: string }) => (
  <span className="disc-avatar" style={{ width: size, height: size, fontSize: Math.round(size * 0.38), background: color }}>
    {text}
  </span>
);

const DiscussionsPage = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'all' | 'tagged-me' | 'i-tagged'>('all');
  const [taggedMe, setTaggedMe] = useState<InboxItem[]>([]);
  const [iTagged, setITagged] = useState<InboxItem[]>([]);
  const [allThreads, setAllThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(true);

  // List controls
  const [sortBy, setSortBy] = useState<SortKey>('recent');

  // Thread detail view
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [threadComments, setThreadComments] = useState<Comment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);

  // Reply / compose
  const [replyText, setReplyText] = useState('');
  const [replyMentionIds, setReplyMentionIds] = useState<string[]>([]);
  const [replyParentId, setReplyParentId] = useState<string | null>(null);
  const [replying, setReplying] = useState(false);

  // Edit
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  // New thread modal
  const [newThreadOpen, setNewThreadOpen] = useState(false);
  const [threadForm] = Form.useForm();
  const [creating, setCreating] = useState(false);
  const [threadBody, setThreadBody] = useState('');
  const [threadTaggedUsers, setThreadTaggedUsers] = useState<string[]>([]);
  const [opportunities, setOpportunities] = useState<{ id: string; title: string }[]>([]);
  const [oppsLoading, setOppsLoading] = useState(false);
  const [allUsers, setAllUsers] = useState<{ id: string; fullName: string; role?: string }[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tagged, iTag, threads] = await Promise.all([
        discussionApi.getTaggedMe(),
        discussionApi.getITagged(),
        discussionApi.getAllThreads(),
      ]);
      setTaggedMe(tagged);
      setITagged(iTag);
      setAllThreads(threads);
    } catch (err) {
      console.error('Failed to load discussions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    userApi.getCurrentUser().then((u: any) => setCurrentUserId(u.id)).catch(() => {});
  }, [fetchData]);

  // Auto-select thread from URL param (e.g. from notification link)
  useEffect(() => {
    const threadId = searchParams.get('thread');
    if (threadId && !loading && allThreads.length > 0) {
      setActiveTab('all');
      setSelectedThreadId(threadId);
      fetchComments(threadId);
      loadUsers();
    }
  }, [searchParams, loading, allThreads.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchComments = async (threadId: string) => {
    setCommentsLoading(true);
    try {
      const data = await discussionApi.getComments(threadId);
      setThreadComments(data);
    } catch {
      message.error('Failed to load comments');
    } finally {
      setCommentsLoading(false);
    }
  };

  const closeThread = () => {
    setSelectedThreadId(null);
    setThreadComments([]);
    setReplyParentId(null);
    setReplyText('');
    setReplyMentionIds([]);
  };

  const handleSelectThread = (threadId: string) => {
    // Clicking the already-open thread closes it.
    if (selectedThreadId === threadId) {
      closeThread();
      return;
    }
    setSelectedThreadId(threadId);
    setReplyParentId(null);
    setReplyText('');
    setReplyMentionIds([]);
    fetchComments(threadId);
    if (allUsers.length === 0) loadUsers();
  };

  const handleReply = async (threadId: string) => {
    if (!replyText.trim()) return;
    setReplying(true);
    try {
      await discussionApi.addComment(threadId, {
        body: replyText,
        parentId: replyParentId || undefined,
        mentionedUserIds: replyMentionIds.length > 0 ? replyMentionIds : undefined,
      });
      setReplyText('');
      setReplyMentionIds([]);
      setReplyParentId(null);
      fetchComments(threadId);
      fetchData();
    } catch {
      message.error('Failed to add comment');
    } finally {
      setReplying(false);
    }
  };

  const handleResolve = async (threadId: string) => {
    try {
      await discussionApi.resolveThread(threadId);
      message.success('Thread resolved');
      fetchData();
    } catch {
      message.error('Failed to resolve');
    }
  };

  const handleReopen = async (threadId: string) => {
    try {
      await discussionApi.reopenThread(threadId);
      message.success('Thread reopened');
      fetchData();
    } catch {
      message.error('Failed to reopen');
    }
  };

  const handleDeleteThread = async (threadId: string) => {
    try {
      await discussionApi.deleteThread(threadId);
      message.success('Thread deleted');
      setSelectedThreadId(null);
      setThreadComments([]);
      fetchData();
    } catch {
      message.error('Failed to delete thread');
    }
  };

  const handleEditComment = async (commentId: string, threadId: string) => {
    if (!editText.trim()) return;
    try {
      await discussionApi.editComment(commentId, editText);
      setEditingCommentId(null);
      setEditText('');
      fetchComments(threadId);
    } catch {
      message.error('Failed to edit comment');
    }
  };

  const handleDeleteComment = async (commentId: string, threadId: string) => {
    try {
      await discussionApi.deleteComment(commentId);
      message.success('Comment deleted');
      fetchComments(threadId);
      fetchData();
    } catch {
      message.error('Failed to delete comment');
    }
  };

  const loadOpportunities = async () => {
    setOppsLoading(true);
    try {
      const data = await opportunityApi.getAll({ size: 200 });
      setOpportunities((data.content || []).map((o: any) => ({ id: o.id, title: o.title })));
    } catch (err) {
      console.error('Failed to load opportunities:', err);
      message.error('Failed to load opportunities');
    } finally {
      setOppsLoading(false);
    }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const data = await discussionApi.getUsers();
      setAllUsers(data);
    } catch (err) {
      console.error('Failed to load users:', err);
      message.error('Failed to load people list');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleNewThread = () => {
    setNewThreadOpen(true);
    if (opportunities.length === 0) loadOpportunities();
    if (allUsers.length === 0) loadUsers();
  };

  const handleCreateThread = async () => {
    setCreating(true);
    try {
      const values = threadForm.getFieldsValue();
      const payload: Record<string, any> = {};
      const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (values.entityId && isValidUUID.test(values.entityId)) {
        payload.entityType = 'OPPORTUNITY';
        payload.entityId = values.entityId;
      }
      if (values.subject) payload.subject = values.subject;
      if (threadBody?.trim()) payload.body = threadBody;
      if (threadTaggedUsers.length > 0) payload.mentionedUserIds = threadTaggedUsers;
      const result = await discussionApi.createThread(payload as any);
      message.success('Thread created');
      setNewThreadOpen(false);
      threadForm.resetFields();
      setThreadBody('');
      setThreadTaggedUsers([]);
      await fetchData();
      if (result?.id) handleSelectThread(result.id);
    } catch (err: any) {
      console.error('Thread creation failed:', err);
      const detail = err?.response?.data?.message || err?.message || 'Unknown error';
      message.error('Failed to create thread: ' + detail);
    } finally {
      setCreating(false);
    }
  };

  const userOptions = allUsers
    .filter((u) => u.id !== currentUserId)
    .map((u) => ({
      value: u.id,
      label: `${u.fullName}${u.role ? ` (${u.role.replace(/_/g, ' ')})` : ''}`,
    }));

  // --- Build normalized list entries for the active tab ---

  const buildEntries = (): ListEntry[] => {
    if (activeTab === 'all') {
      return allThreads.map((t) => ({
        id: t.id,
        subject: t.subject || 'Untitled Thread',
        resolved: !!t.isResolved,
        count: t.commentCount,
        ts: t.lastCommentAt || t.createdAt,
        metaLeft: [t.createdBy, labelize(t.entityType)].filter(Boolean).join(' · '),
        avatarText: getInitials(t.createdBy),
        avatarColor: 'var(--color-primary)',
      }));
    }
    const inboxItems = activeTab === 'tagged-me' ? taggedMe : iTagged;
    const groups = new Map<string, InboxItem[]>();
    for (const item of inboxItems) {
      const g = groups.get(item.threadId) || [];
      g.push(item);
      groups.set(item.threadId, g);
    }
    return Array.from(groups.entries()).map(([threadId, items]) => {
      const first = items[0];
      const prefix =
        activeTab === 'tagged-me'
          ? `${first.authorName} mentioned you`
          : `You tagged ${[...new Set(items.map((i) => i.mentionedUserName))].join(', ')}`;
      return {
        id: threadId,
        subject: first.threadSubject || 'Untitled Thread',
        resolved: !!first.threadResolved,
        count: items.length,
        ts: first.commentCreatedAt,
        metaLeft: labelize(first.entityType),
        avatarText: first.authorInitials || getInitials(first.authorName),
        avatarColor: 'var(--color-purple)',
        preview: first.commentBody,
        prefix,
      };
    });
  };

  const allEntries = buildEntries();

  const visibleEntries = [...allEntries].sort((a, b) => {
    if (sortBy === 'unresolved' && a.resolved !== b.resolved) return a.resolved ? 1 : -1;
    const diff = dayjs(b.ts).valueOf() - dayjs(a.ts).valueOf();
    return sortBy === 'oldest' ? -diff : diff;
  });

  const selectedEntry = allEntries.find((e) => e.id === selectedThreadId);

  // Unique participants across the loaded comment tree (for the detail header).
  const participants: { id: string; initials: string; name: string }[] = [];
  const seenAuthors = new Set<string>();
  const walkAuthors = (list: Comment[]) => {
    list.forEach((c) => {
      if (!seenAuthors.has(c.authorId)) {
        seenAuthors.add(c.authorId);
        participants.push({ id: c.authorId, initials: c.authorInitials, name: c.authorName });
      }
      if (c.replies?.length) walkAuthors(c.replies);
    });
  };
  walkAuthors(threadComments);

  // --- Render: a single comment (recursive) ---

  const renderComment = (comment: Comment, threadId: string) => (
    <div key={comment.id}>
      <div className="disc-comment">
        <Avatar text={comment.authorInitials} size={32} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <Text strong style={{ fontSize: 13 }}>{comment.authorName}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>{dayjs(comment.createdAt).fromNow()}</Text>
            {comment.isEdited && <Tag style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: 0 }}>edited</Tag>}
          </div>
          {editingCommentId === comment.id ? (
            <div>
              <Input.TextArea value={editText} onChange={(e) => setEditText(e.target.value)} rows={2} autoFocus style={{ fontSize: 13, marginBottom: 6 }} />
              <Space size={4}>
                <Button size="small" type="primary" onClick={() => handleEditComment(comment.id, threadId)}>Save</Button>
                <Button size="small" onClick={() => { setEditingCommentId(null); setEditText(''); }}>Cancel</Button>
              </Space>
            </div>
          ) : (
            <>
              <div className="disc-comment__body">{comment.body}</div>
              {comment.mentions?.length > 0 && (
                <div style={{ marginTop: 5 }}>
                  {comment.mentions.map((m) => (
                    <Tag key={m.userId} color="blue" style={{ fontSize: 10, lineHeight: '16px', padding: '0 6px' }}>@{m.fullName}</Tag>
                  ))}
                </div>
              )}
              <Space className="disc-comment__actions" size={10} style={{ marginTop: 4 }}>
                <Button type="link" size="small" style={{ fontSize: 11, padding: 0, height: 'auto' }}
                  onClick={() => { setReplyParentId(comment.id); setReplyText(''); setReplyMentionIds([]); }}>Reply</Button>
                <Button type="link" size="small" style={{ fontSize: 11, padding: 0, height: 'auto' }} icon={<EditOutlined />}
                  onClick={() => { setEditingCommentId(comment.id); setEditText(comment.body); }} />
                <Popconfirm title="Delete this comment?" onConfirm={() => handleDeleteComment(comment.id, threadId)} okText="Delete" okType="danger">
                  <Button type="link" size="small" danger style={{ fontSize: 11, padding: 0, height: 'auto' }} icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            </>
          )}
        </div>
      </div>
      {(comment.replies?.length > 0 || replyParentId === comment.id) && (
        <div className="disc-comment__replies">
          {comment.replies?.map((reply) => renderComment(reply, threadId))}
          {replyParentId === comment.id && (
            <div style={{ paddingTop: 4 }}>
              <Select mode="multiple" placeholder="Tag people (optional)" value={replyMentionIds}
                onChange={setReplyMentionIds} showSearch optionFilterProp="label" options={userOptions}
                style={{ width: '100%', marginBottom: 6 }} size="small" />
              <Input.TextArea value={replyText} onChange={(e) => setReplyText(e.target.value)}
                placeholder={`Reply to ${comment.authorName}...`} rows={2} autoFocus style={{ fontSize: 13 }} />
              <Space size={6} style={{ marginTop: 6 }}>
                <Button size="small" type="primary" icon={<SendOutlined />} loading={replying}
                  onClick={() => handleReply(threadId)} disabled={!replyText.trim()}>Reply</Button>
                <Button size="small" onClick={() => { setReplyParentId(null); setReplyText(''); setReplyMentionIds([]); }}>Cancel</Button>
              </Space>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // --- Render: the detail pane (right side) ---

  const renderDetail = () => {
    if (!selectedEntry) {
      return (
        <div className="disc-detail-empty">
          <MessageOutlined style={{ fontSize: 40, color: 'var(--color-text-disabled)' }} />
          <div>
            <div style={{ fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 4 }}>No conversation selected</div>
            <Text type="secondary" style={{ fontSize: 13 }}>Pick a thread from the list to read and reply.</Text>
          </div>
        </div>
      );
    }
    const { id: threadId, resolved } = selectedEntry;
    return (
      <>
        <div className="disc-detail-head">
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Button className="disc-back" type="text" size="small" icon={<ArrowLeftOutlined />} onClick={closeThread} />
              <span className="disc-dot" style={{ background: resolved ? 'var(--color-success)' : 'var(--color-primary)' }} />
              <Text strong style={{ fontSize: 16 }} ellipsis>{selectedEntry.subject}</Text>
              {resolved ? (
                <Tag color="success" style={{ margin: 0 }}><CheckCircleOutlined /> Resolved</Tag>
              ) : (
                <Tag color="blue" style={{ margin: 0 }}>Open</Tag>
              )}
            </div>
            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {selectedEntry.metaLeft || 'General'} · {dayjs(selectedEntry.ts).fromNow()}
              </Text>
              {participants.length > 0 && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span className="disc-avatars">
                    {participants.slice(0, 4).map((p) => (
                      <Tooltip key={p.id} title={p.name}>
                        <span><Avatar text={p.initials} size={22} color="var(--color-purple)" /></span>
                      </Tooltip>
                    ))}
                  </span>
                  {participants.length > 4 && <Text type="secondary" style={{ fontSize: 11 }}>+{participants.length - 4}</Text>}
                </span>
              )}
            </div>
          </div>
          <Space size={6} style={{ flexShrink: 0 }}>
            {resolved ? (
              <Button size="small" icon={<UndoOutlined />} onClick={() => handleReopen(threadId)}>Reopen</Button>
            ) : (
              <Button size="small" type="primary" ghost icon={<CheckCircleOutlined />} onClick={() => handleResolve(threadId)}>Resolve</Button>
            )}
            <Popconfirm title="Delete this entire thread?" description="All comments and mentions will be removed." onConfirm={() => handleDeleteThread(threadId)} okText="Delete" okType="danger">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
            <Tooltip title="Close">
              <Button size="small" type="text" icon={<CloseOutlined />} onClick={closeThread} />
            </Tooltip>
          </Space>
        </div>

        <div className="disc-detail-scroll">
          {commentsLoading ? (
            <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
          ) : threadComments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 24, color: 'var(--color-text-tertiary)', fontSize: 13 }}>
              No comments yet — start the conversation below.
            </div>
          ) : (
            threadComments.map((comment) => renderComment(comment, threadId))
          )}
        </div>

        <div className="disc-composer">
          <Select mode="multiple" placeholder="Tag people (optional)" value={replyParentId === null ? replyMentionIds : []}
            onChange={(v) => { setReplyParentId(null); setReplyMentionIds(v); }} showSearch optionFilterProp="label"
            options={userOptions} style={{ width: '100%', marginBottom: 6 }} size="small" />
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <Input.TextArea
              value={replyParentId === null ? replyText : ''}
              onChange={(e) => { setReplyParentId(null); setReplyText(e.target.value); }}
              onFocus={() => { if (replyParentId !== null) setReplyParentId(null); }}
              placeholder="Add a comment…"
              autoSize={{ minRows: 1, maxRows: 5 }}
              style={{ fontSize: 13, flex: 1 }}
            />
            <Button type="primary" icon={<SendOutlined />} loading={replying}
              onClick={() => handleReply(threadId)} disabled={replyParentId !== null || !replyText.trim()}>
              Send
            </Button>
          </div>
        </div>
      </>
    );
  };

  // --- Render: the list pane (left side) ---

  const renderList = () => {
    if (loading) {
      return (
        <div className="disc-list-scroll">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} style={{ padding: '11px 12px' }}>
              <div className="ui-skeleton" style={{ width: '60%', height: 13, marginBottom: 8 }} />
              <div className="ui-skeleton" style={{ width: '40%', height: 11 }} />
            </div>
          ))}
        </div>
      );
    }
    if (allEntries.length === 0) {
      return (
        <div style={{ padding: 16 }}>
          <EmptyState
            icon={activeTab === 'all' ? <MessageOutlined /> : activeTab === 'tagged-me' ? <TagOutlined /> : <UserOutlined />}
            title={
              activeTab === 'all' ? 'No threads yet'
                : activeTab === 'tagged-me' ? 'No one has tagged you yet'
                : "You haven't tagged anyone yet"
            }
            description={
              activeTab === 'all' ? 'Start a discussion to tag teammates and talk through tasks, gates, and opportunities.'
                : activeTab === 'tagged-me' ? 'Threads where others @mention you will appear here.'
                : 'Threads where you @mention others will appear here.'
            }
          />
        </div>
      );
    }
    return (
      <div className="disc-list-scroll">
        {visibleEntries.map((e) => (
          <button
            key={e.id}
            type="button"
            className="disc-item ui-focusable"
            data-active={selectedThreadId === e.id}
            onClick={() => handleSelectThread(e.id)}
          >
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <Avatar text={e.avatarText} size={34} color={e.avatarColor} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="disc-item__top">
                  {!e.resolved && <span className="disc-dot" style={{ background: 'var(--color-primary)' }} />}
                  <span
                    className="disc-item__subject"
                    style={e.resolved ? { color: 'var(--color-text-tertiary)' } : undefined}
                  >
                    {e.subject}
                  </span>
                  {e.resolved && (
                    <Tag color="success" style={{ fontSize: 10, lineHeight: '16px', padding: '0 6px', margin: 0, flexShrink: 0 }}>
                      <CheckCircleOutlined style={{ fontSize: 9, marginRight: 2 }} />
                      Resolved
                    </Tag>
                  )}
                  <span className="disc-item__time">{dayjs(e.ts).fromNow(true)}</span>
                </div>
                <div className="disc-item__meta">
                  {e.metaLeft && <span>{e.metaLeft} · </span>}
                  {e.count} {e.count === 1 ? 'message' : 'messages'}
                </div>
                {(e.prefix || e.preview) && (
                  <div className="disc-item__preview">
                    {e.prefix && <span className="disc-item__prefix">{e.prefix}: </span>}
                    {e.preview}
                  </div>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    );
  };

  const tabs: { key: typeof activeTab; label: string; icon: React.ReactNode; count: number }[] = [
    { key: 'all', label: 'All Threads', icon: <UnorderedListOutlined />, count: allThreads.length },
    { key: 'tagged-me', label: 'Tagged Me', icon: <TagOutlined />, count: taggedMe.length },
    { key: 'i-tagged', label: 'I Tagged', icon: <UserOutlined />, count: iTagged.length },
  ];

  return (
    <div>
      <PageHeader
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <MessageOutlined style={{ color: 'var(--color-primary)' }} />
            Discussions
          </span>
        }
        subtitle="Tag anyone across roles to discuss tasks, gates, and opportunities"
        actions={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleNewThread}>
            New Thread
          </Button>
        }
      />

      <div className="disc-layout" data-detail-open={!!selectedThreadId} data-empty={allEntries.length === 0}>
        {/* Left: list */}
        <div className="disc-pane disc-pane--list">
          <div className="disc-list-head">
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className="ui-chip ui-focusable"
                  data-active={activeTab === t.key}
                  onClick={() => { setActiveTab(t.key); setSelectedThreadId(null); }}
                >
                  {t.icon}
                  {t.label}
                  {t.count > 0 && <span className="ui-chip__count">{t.count}</span>}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Select
                size="small"
                value={sortBy}
                onChange={setSortBy}
                style={{ width: 150 }}
                options={[
                  { value: 'recent', label: 'Recent activity' },
                  { value: 'oldest', label: 'Oldest first' },
                  { value: 'unresolved', label: 'Unresolved first' },
                ]}
              />
            </div>
          </div>
          {renderList()}
        </div>

        {/* Right: detail — hidden when the current tab has no threads, so we don't
            show a second empty state next to the list's "No threads yet". */}
        {allEntries.length > 0 && (
          <div className="disc-pane disc-pane--detail">{renderDetail()}</div>
        )}
      </div>

      {/* New Thread Modal */}
      <Modal
        title="Start a New Discussion"
        open={newThreadOpen}
        onOk={handleCreateThread}
        onCancel={() => { setNewThreadOpen(false); threadForm.resetFields(); setThreadBody(''); setThreadTaggedUsers([]); }}
        confirmLoading={creating}
        okText="Create Thread"
        maskClosable={false}
        width={560}
      >
        <Form form={threadForm} layout="vertical">
          <Form.Item label="Tag People">
            <Select
              mode="multiple"
              placeholder="Select people to tag..."
              loading={usersLoading}
              value={threadTaggedUsers}
              onChange={setThreadTaggedUsers}
              showSearch
              optionFilterProp="label"
              options={userOptions}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item name="entityId" label="Opportunity">
            <Select
              placeholder="Select opportunity (optional)..."
              loading={oppsLoading}
              showSearch
              allowClear
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={opportunities.map((o) => ({ value: o.id, label: o.title }))}
            />
          </Form.Item>
          <Form.Item name="subject" label="Subject">
            <Input placeholder="e.g. Compliance question on Section 3" />
          </Form.Item>
          <Form.Item label="Message">
            <Input.TextArea
              value={threadBody}
              onChange={(e) => setThreadBody(e.target.value)}
              placeholder="Type your message..."
              rows={4}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DiscussionsPage;
